import { useEffect, useState } from 'react';
import { Outlet, Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  LayoutDashboard, ShoppingBag, Package, Users, Tag, DollarSign, BarChart3,
  LogOut, ChevronRight, ChevronDown, Menu, X, Truck, ClipboardList,
  Settings, MessageSquare, Store, ExternalLink, Bell, Search
} from 'lucide-react';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetTrigger } from '../../components/ui/sheet';
import { Badge } from '../../components/ui/badge';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../../components/ui/collapsible';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_940e8ae3-c19e-4add-8a26-04b3a3541f3d/artifacts/59pes23f_logocafe.png";

const navSections = [
  {
    title: 'General',
    items: [
      { label: 'Dashboard', to: '/admin', icon: LayoutDashboard },
    ],
  },
  {
    title: 'Gestion de Pedidos',
    items: [
      { label: 'Todos los Pedidos', to: '/admin/pedidos', icon: ShoppingBag, badgeKey: 'pending' },
      { label: 'Produccion', to: '/admin/produccion', icon: ClipboardList, badgeKey: 'production' },
      { label: 'Entregas', to: '/admin/entregas', icon: Truck, badgeKey: 'delivery' },
    ],
  },
  {
    title: 'Catalogo',
    items: [
      { label: 'Productos', to: '/admin/productos', icon: Package },
      { label: 'Promociones', to: '/admin/promociones', icon: Tag },
      { label: 'Tiendas', to: '/admin/tiendas', icon: Store },
    ],
  },
  {
    title: 'Personas',
    items: [
      { label: 'Clientes', to: '/admin/clientes', icon: Users },
      { label: 'Usuarios y Logs', to: '/admin/usuarios', icon: Users },
      { label: 'Proveedores', to: '/admin/proveedores', icon: Store },
    ],
  },
  {
    title: 'Finanzas',
    items: [
      { label: 'Pagos y Gastos', to: '/admin/finanzas', icon: DollarSign },
      { label: 'Cuadre de Caja', to: '/admin/caja', icon: DollarSign },
      { label: 'Reportes', to: '/admin/reportes', icon: BarChart3 },
    ],
  },
  {
    title: 'Sistema',
    items: [
      { label: 'Encuestas', to: '/admin/encuestas', icon: MessageSquare },
      { label: 'Configuracion', to: '/admin/configuracion', icon: Settings },
    ],
  },
];

function SidebarNav({ location, badges, onNavigate }) {
  const { user } = useAuth();
  const [openSections, setOpenSections] = useState(() => {
    const initial = {};
    navSections.forEach(s => { initial[s.title] = true; });
    return initial;
  });

  const toggleSection = (title) => {
    setOpenSections(prev => ({ ...prev, [title]: !prev[title] }));
  };

  const filteredSections = navSections.map(section => {
    const isVendedor = user?.role === 'vendedora' || user?.role === 'vendedor';
    const isProduccion = user?.role === 'produccion' || user?.role === 'producción';
    const isDomiciliario = user?.role === 'domiciliario';

    if (isDomiciliario) {
      if (section.title === 'Gestion de Pedidos') {
        return {
          ...section,
          items: section.items.filter(item => item.to === '/admin/entregas')
        };
      }
      return null;
    }
    
    if (isProduccion) {
      if (section.title === 'Gestion de Pedidos') {
        return {
          ...section,
          items: section.items.filter(item => item.to === '/admin/produccion')
        };
      }
      return null;
    }

    if (isVendedor) {
      const filteredItems = section.items.filter(item => 
        item.to !== '/admin/configuracion' && item.to !== '/admin/usuarios'
      );
      if (filteredItems.length === 0) return null;
      return {
        ...section,
        items: filteredItems
      };
    }

    return section;
  }).filter(Boolean);

  return (
    <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto" data-testid="admin-nav">
      {filteredSections.map(section => (
        <Collapsible key={section.title} open={openSections[section.title]} onOpenChange={() => toggleSection(section.title)}>
          <CollapsibleTrigger className="flex items-center justify-between w-full px-3 py-2 group cursor-pointer" data-testid={`section-${section.title.toLowerCase().replace(/ /g, '-')}`}>
            <span className="font-body text-[10px] tracking-[0.15em] uppercase font-bold text-[#75664d]/60">{section.title}</span>
            <ChevronDown className={`w-3 h-3 text-[#75664d]/40 transition-transform ${openSections[section.title] ? '' : '-rotate-90'}`} />
          </CollapsibleTrigger>
          <CollapsibleContent className="space-y-0.5">
            {section.items.map(item => {
              const isActive = location.pathname === item.to || (item.to !== '/admin' && location.pathname.startsWith(item.to));
              const badgeCount = item.badgeKey ? badges[item.badgeKey] : 0;
              return (
                <Link
                  key={item.to}
                  to={item.to}
                  onClick={onNavigate}
                  data-testid={`nav-${item.label.toLowerCase().replace(/ /g, '-')}`}
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-xl font-body text-sm transition-all ${
                    isActive
                      ? 'bg-[#a0d5ba]/20 text-[#46371c] font-medium shadow-sm'
                      : 'text-[#75664d] hover:bg-[#f4f1ea] hover:text-[#46371c]'
                  }`}
                >
                  <item.icon className={`w-[18px] h-[18px] shrink-0 ${isActive ? 'text-[#46371c]' : 'text-[#75664d]'}`} />
                  <span className="flex-1 truncate">{item.label}</span>
                  {badgeCount > 0 && (
                    <Badge className="bg-[#a0d5ba] text-[#46371c] border-0 text-[10px] h-5 min-w-[20px] flex items-center justify-center rounded-full px-1.5">{badgeCount}</Badge>
                  )}
                </Link>
              );
            })}
          </CollapsibleContent>
        </Collapsible>
      ))}
    </nav>
  );
}

export default function AdminLayout() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [badges, setBadges] = useState({ pending: 0, production: 0, delivery: 0 });

  useEffect(() => {
    if (!loading) {
      const validRoles = ['admin', 'domiciliario', 'produccion', 'producción', 'vendedora', 'vendedor'];
      const isVendedor = user && (user.role === 'vendedora' || user.role === 'vendedor');
      const isProduccion = user && (user.role === 'produccion' || user.role === 'producción');

      if (!user || !validRoles.includes(user.role)) {
        navigate('/admin/login');
      } else if (user.role === 'domiciliario' && location.pathname !== '/admin/entregas') {
        navigate('/admin/entregas');
      } else if (isProduccion && location.pathname !== '/admin/produccion') {
        navigate('/admin/produccion');
      } else if (isVendedor && (location.pathname === '/admin/configuracion' || location.pathname === '/admin/usuarios')) {
        navigate('/admin');
      }
    }
  }, [user, loading, navigate, location.pathname]);

  // Fetch badge counts
  useEffect(() => {
    if (!user || user.role !== 'admin') return;
    const fetchBadges = async () => {
      try {
        const token = localStorage.getItem('token');
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const { data } = await axios.get(`${API}/admin/dashboard`, { withCredentials: true, headers });
        setBadges({
          pending: data.pending_orders || 0,
          production: data.in_production || 0,
          delivery: 0,
        });
      } catch {}
    };
    fetchBadges();
    const interval = setInterval(fetchBadges, 30000);
    return () => clearInterval(interval);
  }, [user]);

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-[#f4f1ea]">
      <div className="text-center">
        <div className="w-10 h-10 border-2 border-[#a0d5ba] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
        <p className="font-body text-sm text-[#75664d]">Cargando panel...</p>
      </div>
    </div>
  );
  if (!user || !['admin', 'domiciliario', 'vendedora', 'vendedor', 'produccion', 'producción'].includes(user.role)) return null;

  const handleLogout = async () => {
    await logout();
    navigate('/admin/login');
  };

  const totalBadges = badges.pending + badges.production + badges.delivery;

  const sidebarContent = (
    <>
      {/* Logo */}
      <div className="p-5 border-b border-[#e5dfd3]">
        <Link to="/admin" className="flex items-center gap-3">
          <img src={LOGO_URL} alt="Keiks" className="h-9" />
        </Link>
        <div className="flex items-center gap-2 mt-3">
          <div className="w-1.5 h-1.5 rounded-full bg-[#a0d5ba] animate-pulse" />
          <p className="font-body text-[11px] text-[#75664d]">Panel Administrativo</p>
        </div>
      </div>

      {/* Navigation */}
      <SidebarNav location={location} badges={badges} onNavigate={() => setMobileOpen(false)} />

      {/* Footer */}
      <div className="p-4 border-t border-[#e5dfd3] space-y-3">
        <Link to="/" className="flex items-center gap-2 px-3 py-2 rounded-xl font-body text-xs text-[#75664d] hover:bg-[#f4f1ea] hover:text-[#46371c] transition-colors" data-testid="view-store-link">
          <ExternalLink className="w-3.5 h-3.5" /> Ver tienda
        </Link>
        <div className="flex items-center gap-3 px-3 py-2 rounded-xl bg-[#f4f1ea]">
          <div className="w-8 h-8 rounded-full bg-[#a0d5ba]/30 flex items-center justify-center shrink-0">
            <span className="font-heading text-sm font-bold text-[#46371c]">{user?.name?.charAt(0)?.toUpperCase() || 'A'}</span>
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-body text-xs font-medium text-[#46371c] truncate">{user?.name}</p>
            <p className="font-body text-[10px] text-[#75664d] truncate">{user?.email}</p>
          </div>
        </div>
        <button onClick={handleLogout} className="flex items-center gap-2 px-3 py-2 rounded-xl font-body text-sm text-[#75664d] hover:bg-red-50 hover:text-red-500 transition-colors w-full" data-testid="admin-logout-btn">
          <LogOut className="w-4 h-4" /> Cerrar sesion
        </button>
      </div>
    </>
  );

  return (
    <div className="min-h-screen bg-[#f4f1ea] flex" data-testid="admin-layout">
      {/* Desktop Sidebar */}
      <aside className="w-[260px] bg-[#fcfbf9] border-r border-[#e5dfd3] flex flex-col shrink-0 hidden lg:flex" data-testid="admin-sidebar">
        {sidebarContent}
      </aside>

      {/* Mobile Top Bar */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-[#fcfbf9]/95 backdrop-blur-lg border-b border-[#e5dfd3]">
        <div className="flex items-center justify-between px-4 h-14">
          <div className="flex items-center gap-3">
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild>
                <button className="p-2 -ml-2 rounded-lg hover:bg-[#f4f1ea] transition-colors" data-testid="mobile-menu-btn">
                  <Menu className="w-5 h-5 text-[#46371c]" />
                </button>
              </SheetTrigger>
              <SheetContent side="left" className="w-[280px] p-0 bg-[#fcfbf9] flex flex-col">
                <SheetHeader className="sr-only">
                  <SheetTitle>Menu de Administracion</SheetTitle>
                  <SheetDescription>Navegacion del panel administrativo</SheetDescription>
                </SheetHeader>
                {sidebarContent}
              </SheetContent>
            </Sheet>
            <Link to="/admin"><img src={LOGO_URL} alt="Keiks" className="h-7" /></Link>
          </div>
          <div className="flex items-center gap-1">
            {totalBadges > 0 && (
              <Link to="/admin/pedidos" className="relative p-2 rounded-lg hover:bg-[#f4f1ea]">
                <Bell className="w-5 h-5 text-[#46371c]" />
                <Badge className="absolute -top-0.5 -right-0.5 bg-[#a0d5ba] text-[#46371c] border-0 text-[9px] h-4 min-w-[16px] flex items-center justify-center rounded-full px-1">{totalBadges}</Badge>
              </Link>
            )}
            <button onClick={handleLogout} className="p-2 rounded-lg hover:bg-[#f4f1ea]">
              <LogOut className="w-5 h-5 text-[#75664d]" />
            </button>
          </div>
        </div>
      </div>

      {/* Main content */}
      <main className="flex-1 overflow-auto">
        <div className="lg:p-8 p-4 pt-[72px] lg:pt-8 max-w-[1400px]">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
